import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, X, Sparkles, ArrowRight, Bot, Compass, ListChecks } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import * as sample from '../agent/sampleData';
import { isBackendEnabled, chatViaBackend, backendHealth } from '../agent/backendClient';
import appConfig from '../config/appConfig';

/**
 * ChatAssistant — the "Central Health Provider Portal Assistant".
 * ========================================================
 * Login-gated (mounted inside MainLayout). Helps clinic staff look up claims,
 * payments, members (patients), eligibility, and portal activity, and walks
 * them through common self-service tasks. Prior authorizations and gold-carding
 * are NOT part of this portal — the assistant redirects such questions to
 * Provider Services.
 *
 * Two modes, in priority order:
 *   • backend (VITE_AGENT_URL set): the EIX-COPILOT agent backend runs a
 *     read-only tool loop server-side (the browser holds no LLM key / PHI
 *     logic) and returns a grounded { answer, cards, cta }. This is the
 *     production path. Any backend/LLM failure falls back to offline.
 *   • offline (default): a chip-driven menu + keyword engine over bundled
 *     sample data. Zero setup, so the demo always works.
 *
 * READ-ONLY: self-service actions are surfaced as steps + deep links, never
 * performed by the assistant.
 */

const COLOR = { primary: '#0D9488', dark: '#0F766E', deep: '#134E4A' };

// Re-trigger the guided tour (AppTour) — it consumes this flag on '/'.
const TOUR_START_KEY = 'sendero_tour_start';
const TOUR_DONE_KEY = 'sendero_tour_completed';
const TOUR_CHIP = 'Take a quick tour';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

//const { user, logout, isImpersonating, originalUser, endImpersonation, signalRLogout } = useAuth();


const user = JSON.parse(localStorage.getItem('sendero_provider_user'));



const userRole = user?.userRole?.trim().toLowerCase();

console.log("USER ROLE AT ChatAssistant : "+userRole);

//alert(userRole);



/* ── Chips ───────────────────────────────────────────────────────────── */
const TOP_CHIPS = ['Search claims', 'Check eligibility', 'Payments', 'Get help with a task', TOUR_CHIP];
const TASK_CHIPS = [
    'Check a claim status',
    'Why was a claim denied?',
    'File a paper claim',
    'Where to send paper claims',
    'Clearinghouse & payer ID',
    'Prior auth / referral instructions',
    'Appeal a claim',
    //'Sign up for EFT',
    //'Sign up for ERA',
    'Verify eligibility',
    'Find an EOP',
    'Download a form',
    //'Add a user',
    'Change password',
   // 'Add a Tax ID',
    'Recover username',
    'Contact Provider Services',
    'What can the portal do?',

    ...(userRole === 'provider admin'
        ? ['Add a user', 'Add a Tax ID', 'Sign up for EFT', 'Sign up for ERA']
        : []),

    //...(userRole === 'provider admin' || userRole === 'super admin'
    //    ? ['Sign up for EFT', 'Sign up for ERA']
    //    : []),

    ...(userRole === 'super admin' 
        ? ['Add Super admin user', 'System settings', 'Feedback', 'Sign up for EFT', 'Sign up for ERA']
        : []),
];

// Which self-service guide a task chip maps to.
const TASK_CHIP_TOPIC = {
  'Check a claim status': 'check_claim_status',
  'File a paper claim': 'file_paper_claim',
  'Where to send paper claims': 'paper_claim_address',
  'Clearinghouse & payer ID': 'clearinghouse_payer_id',
  'Prior auth / referral instructions': 'prior_auth_referral',
  'Appeal a claim': 'claim_appeal',
  'Sign up for EFT': 'eft_enrollment',
  'Sign up for ERA': 'era_enrollment',
  'Verify eligibility': 'check_eligibility',
  'Find an EOP': 'find_eop',
  'Download a form': 'download_form',
  'Add a user': 'add_portal_user',
  'Change password': 'reset_password',
  'Add a Tax ID': 'add_tax_id',
  'Recover username': 'forgot_username',
  'Contact Provider Services': 'contact_support',
  'What can the portal do?': 'portal_overview',
  'Add Super admin user': 'system_users',
  'Feedback': 'feedback'
};

// Per-page walkthroughs: one chip per sidebar menu / submenu item.
const BROWSE_PAGES_CHIP = 'Guide me through a page';
const MENU_CHIPS = sample.MENU_GUIDES.map((m) => m.label);
const MENU_CHIP_TOPIC = Object.fromEntries(sample.MENU_GUIDES.map((m) => [m.label, m.topic]));

// Only offer page walkthroughs the signed-in user can actually reach: admin
// pages are hidden from the chip list for roles that don't see them in the nav.
function menuChipsForRole(role) {
  const r = String(role || '').toUpperCase();
  //return sample.MENU_GUIDES
  //  .filter((m) => (!m.superAdminOnly || r === 'SUPER ADMIN') && (!m.providerAdminOnly || r === 'PROVIDER ADMIN'))
    //  .map((m) => m.label);

    return sample.MENU_GUIDES
        .filter((m) => {
            if (m.superAdminOnly && r !== 'SUPER ADMIN') {
                return false;
            }

            if (m.providerAdminOnly && r !== 'PROVIDER ADMIN') {
                return false;
            }

            if (
                m.providerAdminAndSuperAdminOnly &&
                !['PROVIDER ADMIN', 'SUPER ADMIN'].includes(r)
            ) {
                return false;
            }

            if (m.providerAdminAndOwnUserOnly && r === 'SUPER ADMIN') {
                return false;
            }

            return true;
        })
        .map((m) => m.label);
}

/* Status → badge colour. */
function statusTone(status = '') {
  const s = String(status).toLowerCase();
  if (['paid', 'approved', 'completed', 'active'].includes(s)) return { bg: '#ECFDF5', fg: '#059669' };
  if (['pending', 'review', 'processing'].includes(s)) return { bg: '#FFFBEB', fg: '#B45309' };
  if (['denied', 'overdue', 'failed', 'terminated', 'expired'].includes(s)) return { bg: '#FEF2F2', fg: '#DC2626' };
  return { bg: '#F1F5F9', fg: '#475569' };
}

/* -- record card builders (map raw records → display cards) -------------- */
const claimCard = (c) => ({
  title: `Claim ${c.claimNumber}`,
  subtitle: `${c.memberName || ''}${c.memberId ? ` · ${c.memberId}` : ''}`,
  badge: c.status,
  rows: [
    ['Service date', c.serviceDate || '—'],
    ['Billed', money(c.billedAmount)],
    ['Plan paid', money(c.paidAmount)],
    ['Member resp.', money(c.memberResponsibility)],
    ['CPT / Dx', `${c.procedureCode || '—'} / ${c.diagnosisCode || '—'}`],
    ...(c.checkNumber ? [['Check', c.checkNumber]] : []),
    ...(c.eopNumber ? [['EOP', c.eopNumber]] : []),
  ],
  cta: { label: 'Open in Claims', route: '/claims' },
});
const invoiceCard = (i) => ({
  title: i.description || i.claimNumber,
  subtitle: `${i.memberName || ''}${i.memberId ? ` · ${i.memberId}` : ''}`,
  badge: i.status,
  rows: [
    ['Claim', i.claimNumber || '—'],
    ['Amount', money(i.amount)],
    ['Service date', i.dateOfService || '—'],
    ['Due date', i.dueDate || '—'],
  ],
  cta: { label: 'View payments', route: '/invoices/payments' },
});
const memberCard = (m) => ({
  title: `${m.firstName} ${m.lastName}`,
  subtitle: `${m.planName || ''}${m.memberId ? ` · ${m.memberId}` : ''}`,
  badge: m.status,
  rows: [
    ['DOB', m.dateOfBirth || '—'],
    ['Coverage', `${m.eligibilityStart || '—'} → ${m.eligibilityEnd || '—'}`],
    ['Group', m.groupNumber || '—'],
    ['Copays', `${money(m.medicalCopay)} / ${money(m.specialistCopay)} spec.`],
    ['Deductible', money(m.deductible)],
    ['PCP', `${m.pcpName || '—'}${m.pcpPhone ? ` · ${m.pcpPhone}` : ''}`],
  ],
  cta: { label: 'Open Eligibility', route: '/eligibility' },
});

/* -- helpers ------------------------------------------------------------- */
const norm = (x) => String(x || '').toLowerCase().trim();

const CLAIM_RE = /clm-\d{4}-\d{4,6}/i;
const EOP_RE = /eop-\d{4}-\d{4,6}/i;
const CHECK_RE = /chk-\d{4,6}/i;
const MEMBER_RE = /snd-\d{4,6}/i;

// Claim/auth status → canonical key (values in the data are English).
const STATUS_WORDS = {
  paid: 'paid', pending: 'pending', denied: 'denied', approved: 'approved',
  review: 'review', overdue: 'overdue', active: 'active', terminated: 'terminated',
};
function detectStatus(s) {
  for (const w of Object.keys(STATUS_WORDS)) if (s.includes(w)) return STATUS_WORDS[w];
  return null;
}

// Which self-service guide does this text ask about? (null if none)
function detectStepsTopic(s) {
  // Account help (check these first — short, unambiguous phrases).
  if (/(add|new|another|additional|extra|second|multiple|register|link).*(tax ?id|tin\b|ein\b)/.test(s) || /(tax ?id|tin\b|ein\b).*(add|new|another|additional|how)/.test(s)) return 'add_tax_id';
  if (/(reset|change|forgot|update|recover).*(password|passcode)/.test(s) || /password.*(reset|change|forgot|help)/.test(s)) return 'reset_password';
  if (/(forgot|recover|retrieve|find|reset|change).*(username|user name|login|user id)/.test(s) || /username.*(forgot|recover|reset|help)/.test(s)) return 'forgot_username';
  // EFT / ERA enrollment.
  if (/\beft\b|direct deposit|electronic funds/.test(s) && /(sign up|enroll|register|set up|setup|how|form)/.test(s)) return 'eft_enrollment';
  if (/\bera\b|835|remittance advice/.test(s) && /(sign up|enroll|register|set up|setup|how|form)/.test(s)) return 'era_enrollment';
  if (/\beft\b|direct deposit/.test(s)) return 'eft_enrollment';
  if (/\bera\b|835 remittance/.test(s)) return 'era_enrollment';
  // Claims workflows.
  if (/(appeal|reconsider|reconsideration|dispute)/.test(s) && /(claim|denial|denied|payment)/.test(s)) return 'claim_appeal';
  // Plan contact facts: where to mail claims, payer ID / clearinghouse, prior auth or referral instructions.
  if (/(where|address|mail(ing)? address|send|po box|p\.o\.).*(claim)/.test(s) || /(claim).*(address|where to send|mail to|po box)/.test(s)) return 'paper_claim_address';
  if (/(payer ?id|clearing ?house|clearinghouse|payor ?id|edi (id|number)|837)/.test(s)) return 'clearinghouse_payer_id';
  if (/(prior ?auth|pre.?auth|authorization|referral)/.test(s)) return 'prior_auth_referral';
  if (/(paper|mail|cms.?1500|ub.?04).*(claim)/.test(s) || /claim.*(paper|by mail|mail|cms.?1500|ub.?04)/.test(s)) return 'file_paper_claim';
  if (/(check|status|track|where).*(claim)/.test(s) || /claim.*(status|how)/.test(s)) return 'check_claim_status';
  if (/(verify|check|confirm).*(eligib|coverage|benefit)/.test(s) || /(eligib).*(how|verify|check)/.test(s)) return 'check_eligibility';
  if (/(eop|remittance|explanation of payment)/.test(s) && /(find|search|download|get|how)/.test(s)) return 'find_eop';
  if (/(download|get|find).*(form|reconsideration)/.test(s) || /form.*(download|where|how)/.test(s)) return 'download_form';
  if (/(add|invite|create|new).*(user|staff|account|login)/.test(s) || /user.*(add|invite|how)/.test(s)) return 'add_portal_user';
  // Page walkthroughs (menu / submenu). Guarded so bare record queries still work.
  if (/(dashboard|home page|home screen|home tab)/.test(s)) return 'dashboard_overview';
  if (/(contact & resources|contact and resources|contact page)/.test(s)) return 'contact_resources';
  if (/(provider services|provider support|customer service|phone number|who do i (call|contact)|how do i (call|contact|reach)|contact you|support number)/.test(s)) return 'contact_support';
  if (/(what can (the portal|this portal|you) do|what does the portal|portal features|what is this portal)/.test(s)) return 'portal_overview';
  if (/(system settings|enable|disable|turn (on|off)).*(assistant|chat)/.test(s) || /\bsystem settings\b/.test(s)) return 'system_settings';
  if (/\bsystem users\b/.test(s)) return 'system_users';
  if (/\bfeedback\b/.test(s)) return 'feedback';
  if (/(user management|manage users|manage portal users)/.test(s)) return 'user_management';  
  if (/(my appeals|appeal status|track.*appeal|appeals? (list|page))/.test(s)) return 'my_appeals';
  if (/(profile page|my profile|update.*(profile|my info|my details)|notification preference)/.test(s)) return 'update_profile';
  if (/(help (page|center|desk|& support|and support)|customer support|\bfaq\b)/.test(s)) return 'help_support';
  if (/(how|use|navigate|guide|walk|where).*(eligibility|benefits)/.test(s)) return 'eligibility_benefits';
  if (/(how|use|navigate|guide|walk|where).*(claims? ?& ?payments|claims page|payments page)/.test(s)) return 'claims_payments';
  if (/(how|use|navigate|guide|walk|where).*(eop)/.test(s)) return 'eop_search';
  return null;
}

/* -- claim-explanation detection ----------------------------------------- */
// "Why wasn't this paid / why was it denied / partially paid?" intent.
function isWhyClaim(s) {
  return /(why|reason|explain|what happened|adjust|adjustment)/.test(s) &&
    /(claim|paid|pay|denied|deny|denial|reject|partial|short|less|not full|underpaid|write.?off)/.test(s);
}

// Build a numbered self-service walkthrough.
function buildSteps(topic) {
  const guide = sample.selfServiceSteps[topic];
  if (!guide) return null;
  return {
    // Verb-led titles ("Check a claim status") read as "Here's how to …"; noun
    // titles ("Where to send paper claims", "Clearinghouse & payer ID") get a
    // neutral intro so the sentence stays grammatical.
    text: guide.intro || (/^(where|clearinghouse|what|who|when)\b/i.test(guide.title)
      ? `${guide.title}:`
      : `Here's how to ${guide.title.charAt(0).toLowerCase()}${guide.title.slice(1)}:`),
    steps: guide.steps,
    stepsTitle: guide.title,
    cta: { label: guide.cta, route: guide.route },
  };
}

// Every offline record card carries this so sample answers can't be mistaken
// for live portal data.
const SAMPLE_NOTE = 'Sample data — not a live record.';
function labelSampleCards(res) {
  if (res?.cards?.length) res.cards = res.cards.map((c) => ({ ...c, note: c.note || SAMPLE_NOTE }));
  return res;
}

/**
 * The offline engine. Returns { text, cards?, cta?, chips?, steps?, stepsTitle? }.
 * @param {string} raw
 * @param {{ menuChips?: string[] }} [opts]  menuChips = role-filtered page-walkthrough chips
 */
function runQuery(raw, opts = {}) {
  const s = norm(raw);

  /* 0) Menu tabs -------------------------------------------------------- */
  if (raw === 'Get help with a task') return { text: 'What would you like to do?', chips: [...TASK_CHIPS, BROWSE_PAGES_CHIP] };
  if (raw === BROWSE_PAGES_CHIP) return { text: 'Which page do you want a walkthrough for? I\'ll give step-by-step guidance:', chips: opts.menuChips || MENU_CHIPS };

  /* 0b) Self-service step guides (chips + natural language) ------------- */
  const topic = TASK_CHIP_TOPIC[raw] || MENU_CHIP_TOPIC[raw] || detectStepsTopic(s);
  if (topic) return buildSteps(topic);

  /* 0c) Why was a claim denied / not paid in full? --------------------- */
  if (isWhyClaim(s)) {
    const ref = (raw.match(CLAIM_RE) || raw.match(EOP_RE) || raw.match(CHECK_RE) || [])[0];
    let c = ref ? sample.findClaim(ref) : null;
    if (!c) {
      const st = detectStatus(s);
      if (st) c = sample.claims.find((x) => x.status.toLowerCase() === st);
      if (!c && /(denied|deny|denial|reject)/.test(s)) c = sample.claims.find((x) => x.status.toLowerCase() === 'denied');
      if (!c && /(partial|short|less|not full|underpaid|write.?off)/.test(s)) c = sample.claims.find((x) => x.billedAmount !== x.paidAmount && x.status.toLowerCase() === 'paid');
    }
    if (!c) {
      const withReason = sample.claims.filter((x) => x.explanation);
      return { text: "Which claim? Give me the claim number (CLM-…) and I'll explain the payment decision — for example:", chips: withReason.slice(0, 3).map((x) => `Why was ${x.claimNumber} ${x.status.toLowerCase()}?`) };
    }
    const text = (c.explanation || `Claim ${c.claimNumber} is ${c.status}.`) + (c.nextStep ? `\n\nNext step: ${c.nextStep}` : '');
    return { text, cards: [claimCard(c)], cta: { label: 'Open in Claims', route: '/claims' } };
  }

  /* 1) Direct record lookups ------------------------------------------- */
  const claimM = raw.match(CLAIM_RE) || raw.match(EOP_RE) || raw.match(CHECK_RE);
  if (claimM) {
    const c = sample.findClaim(claimM[0]);
    return c ? { text: "Here's that claim:", cards: [claimCard(c)] }
             : { text: "I couldn't find a claim matching that number.", cta: { label: 'Open Claims', route: '/claims' } };
  }
  /* 2) Eligibility ------------------------------------------------------ */
  if (/(eligib|coverage|is .* (active|covered)|covered)/.test(s)) {
    const m = memberFromText(raw);
    if (m) {
      const eligible = norm(m.status) === 'active';
      return {
        text: `${m.firstName} ${m.lastName} (${m.memberId}) is ${eligible ? 'active and eligible' : `not active — status ${m.status}`} on ${m.planName}, covered ${m.eligibilityStart} → ${m.eligibilityEnd}.`,
        cards: [memberCard(m)],
      };
    }
    return { text: 'Which member? Give me a member ID (SND-…) or name and I\'ll check eligibility.', cta: { label: 'Open Eligibility', route: '/eligibility' } };
  }

  /* 3) Member lookup ---------------------------------------------------- */
  if (/(member|patient|subscriber)/.test(s)) {
    const m = memberFromText(raw);
    if (m) return { text: `Here's ${m.firstName} ${m.lastName}:`, cards: [memberCard(m)] };
    return { text: 'Search a member by ID (SND-…) or name.', cta: { label: 'Open Eligibility', route: '/eligibility' } };
  }

  /* 4) Claims ----------------------------------------------------------- */
  if (/(claim)/.test(s) || raw === 'Search claims') {
    const status = detectStatus(s);
    const member = memberNameFromText(raw);
    const list = sample.filterClaims({ status, member });
    if (!list.length) return { text: 'No claims match that.', cta: { label: 'View claims', route: '/claims' } };
    return {
      text: `Found ${list.length} claim${list.length === 1 ? '' : 's'}${status ? ` with status ${status}` : ''}:`,
      cards: list.slice(0, 5).map(claimCard),
      cta: { label: 'View all claims', route: '/claims' },
    };
  }

  /* 5) Authorization STATUS / gold-carding aren't handled in this portal --
        (how-to-submit questions are answered by the prior_auth_referral guide above) */
  if (/(gold\s*-?\s*card|goldcard)/.test(s) || /\bauth-\d/.test(s)) {
    return { text: 'Authorization status and gold-carding aren\'t available in this portal. For a decision on a submitted request, please reach Provider Services.', cta: { label: 'Open Contact & Resources', route: '/contactres' } };
  }

  /* 6) Payments --------------------------------------------------------- */
  if (/(invoice|bill|payment|balance|owe|outstanding|overdue|remittance)/.test(s) || raw === 'Payments') {
    const status = detectStatus(s);
    const member = memberNameFromText(raw);
    const list = sample.filterInvoices({ status, member });
    if (!list.length) return { text: 'No payments match that.', cta: { label: 'View payments', route: '/invoices/payments' } };
    return {
      text: `Found ${list.length} payment${list.length === 1 ? '' : 's'}${status ? ` (${status})` : ''}:`,
      cards: list.slice(0, 5).map(invoiceCard),
      cta: { label: 'View payments', route: '/invoices/payments' },
    };
  }

  /* 7) Notifications ---------------------------------------------------- */
  if (/(notification|alert|unread|what's new|whats new|message)/.test(s)) {
    const unread = sample.notifications.filter((n) => !n.read);
    return {
      text: unread.length
        ? `You have ${unread.length} unread notification${unread.length === 1 ? '' : 's'}:\n` + unread.map((n) => `• ${n.title}`).join('\n')
        : 'You have no unread notifications.',
      cta: { label: 'Open notifications', route: '/notifications' },
    };
  }

  /* 8) Overview --------------------------------------------------------- */
  if (/(overview|summary|snapshot|dashboard|how are we|how am i doing)/.test(s)) {
    const d = sample.dashboardStats;
    return {
      text: `Portal snapshot (sample data):\n• ${d.activeMembers} active members\n• ${d.pendingClaims} pending claim(s)\n• ${d.outstandingInvoices} outstanding claim(s) · ${money(d.totalPaidThisMonth)} paid this month\n• Claim approval rate: ${d.claimApprovalRate}%`,
      cta: { label: 'Go to dashboard', route: '/' },
    };
  }

  /* 9) Greeting / fallback --------------------------------------------- */
  if (/\b(hi|hello|hey|help|what can you do)\b/.test(s)) {
    return { text: 'Hi! I can look up claims, payments, members, and eligibility, and walk you through common tasks. Pick a topic:', chips: TOP_CHIPS };
  }
  return { text: 'I can help with claims, payments, members, eligibility, and self-service tasks. Pick a topic:', chips: TOP_CHIPS };
}

// Resolve a member from free text (id or name).
function memberFromText(raw) {
  const idM = raw.match(MEMBER_RE);
  if (idM) { const m = sample.findMember(idM[0]); if (m) return m; }
  // Try each member's name against the text.
  const s = norm(raw);
  return sample.members.find((m) =>
    s.includes(norm(`${m.firstName} ${m.lastName}`)) || s.includes(norm(m.lastName))) || null;
}
// Extract a member name/id token to use as a filter (or null).
function memberNameFromText(raw) {
  const m = memberFromText(raw);
  return m ? m.memberId : null;
}

/* -- agent glue (backend { type, record } → display cards) --------------- */
const CARD_BUILDERS = { claim: claimCard, invoice: invoiceCard, member: memberCard };

function toolCardsToCards(toolCards = []) {
  const seen = new Set();
  const out = [];
  for (const { type, record } of toolCards) {
    const build = CARD_BUILDERS[type];
    if (!build || !record) continue;
    const card = build(record);
    if (seen.has(card.title)) continue;
    seen.add(card.title);
    out.push(card);
    if (out.length >= 6) break;
  }
  return out;
}

function buildAgentHistory(history) {
  return history
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.text && m.text.trim())
    .slice(-10)
    .map((m) => ({ role: m.role, content: m.text }));
}

/* -- typewriter ---------------------------------------------------------- */
function Typewriter({ text, onTick, onDone }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setN(0);
    let i = 0;
    const id = setInterval(() => {
      i += 3;
      setN(i);
      onTick?.();
      if (i >= text.length) { clearInterval(id); onDone?.(); }
    }, 16);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return <span style={{ whiteSpace: 'pre-wrap' }}>{text.slice(0, n)}</span>;
}

/* -- record card --------------------------------------------------------- */
function RecordCard({ card, onNavigate }) {
  const tone = statusTone(card.badge);
  return (
    <div style={{ border: '1px solid #E8ECF0', borderRadius: 12, padding: 12, background: '#fff', marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>{card.title}</div>
          {card.subtitle && <div style={{ fontSize: 11.5, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.subtitle}</div>}
        </div>
        {card.badge && <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: tone.bg, color: tone.fg }}>{card.badge}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px' }}>
        {card.rows.map(([k, v], i) => (
          <React.Fragment key={i}>
            <div style={{ fontSize: 11.5, color: '#94A3B8' }}>{k}</div>
            <div style={{ fontSize: 11.5, color: '#334155', fontWeight: 500, textAlign: 'right' }}>{v}</div>
          </React.Fragment>
        ))}
      </div>
      {card.note && (
        <div style={{ marginTop: 8, fontSize: 10.5, lineHeight: 1.4, color: '#94A3B8', fontStyle: 'italic', display: 'flex', gap: 4 }}>
          <span aria-hidden="true">ⓘ</span><span>{card.note}</span>
        </div>
      )}
      {card.cta && (
        <button onClick={() => onNavigate(card.cta.route)} style={{ marginTop: 10, width: '100%', padding: '7px 10px', borderRadius: 8, border: `1px solid ${COLOR.primary}33`, background: `${COLOR.primary}0D`, color: COLOR.dark, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {card.cta.label} <ArrowRight size={13} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

/* -- numbered self-service steps ----------------------------------------- */
function StepsCard({ title, steps }) {
  return (
    <div style={{ border: '1px solid #E8ECF0', borderRadius: 12, padding: 12, background: '#fff', marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12.5, fontWeight: 800, color: '#0F172A' }}>
        <ListChecks size={15} strokeWidth={2.4} color={COLOR.primary} /> {title}
      </div>
      <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((step, i) => (
          <li key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: `${COLOR.primary}14`, color: COLOR.dark, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: '#334155' }}>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* -- main component ------------------------------------------------------ */
export default function ChatAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const menuChips = menuChipsForRole(user?.userRole || user?.role); // role-appropriate page walkthroughs
  // Two modes, in priority order:
  //   backend → server-side agent (VITE_AGENT_URL set) — the production path
  //   offline → keyword/menu engine over sample data (demo; always available)
  const backend = isBackendEnabled();
  const live = backend;
  const [backendModel, setBackendModel] = useState('');
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [workLabel, setWorkLabel] = useState('');
  const scrollRef = useRef(null);

  // Super-admin on/off switch (System Settings). React to live config changes.
  const [enabled, setEnabled] = useState(() => appConfig.getAssistantEnabled());
  useEffect(() => appConfig.subscribe(() => setEnabled(appConfig.getAssistantEnabled())), []);

  // Best-effort: show the backend's model in the header.
  useEffect(() => {
    if (!backend) return;
    let done = false;
    backendHealth().then((h) => { if (!done && h?.model) setBackendModel(h.model); });
    return () => { done = true; };
  }, [backend]);

  const greeting = () => ({ role: 'assistant', text: 'Hi! How can I help? Pick a topic:', chips: TOP_CHIPS, streaming: false });
  const [messages, setMessages] = useState([greeting()]);
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const scrollToEnd = useCallback(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, []);
  useEffect(() => { if (open) scrollToEnd(); }, [messages, thinking, open, scrollToEnd]);

  const startTour = useCallback(() => {
    localStorage.removeItem(TOUR_DONE_KEY);
    localStorage.setItem(TOUR_START_KEY, '1');
    setOpen(false);
    window.dispatchEvent(new Event('sendero:start-tour')); // starts now if already on '/'
    navigate('/');
  }, [navigate]);

  const send = useCallback(async (raw) => {
    const q = (raw ?? '').trim();
    if (!q || thinking) return;
    if (q === TOUR_CHIP) { startTour(); return; }
    setInput('');
    const userMsg = { role: 'user', text: q };
    const history = [...messagesRef.current, userMsg];
    setMessages((prev) => [...prev, userMsg]);

    /* Offline mode */
    if (!live) {
      setThinking(true);
      const delay = 360 + Math.min(q.length * 6, 400);
      setTimeout(() => {
        const res = labelSampleCards(runQuery(q, { menuChips }));
        setThinking(false);
        setMessages((prev) => [...prev, { role: 'assistant', streaming: true, ...res }]);
      }, delay);
      return;
    }

    /* Backend agentic mode — falls back to offline if unreachable. */
    setThinking(true);
    setWorkLabel('Thinking…');
    try {
      const { text, cards, cta } = await chatViaBackend({ messages: buildAgentHistory(history) });
      setThinking(false); setWorkLabel('');
      if (!text) { setMessages((prev) => [...prev, { role: 'assistant', streaming: true, text: 'The assistant returned no output. Check the agent backend / model configuration.' }]); return; }
      setMessages((prev) => [...prev, { role: 'assistant', text, streaming: true, cards: toolCardsToCards(cards), cta }]);
    } catch (err) {
      // Backend/LLM unreachable → offline sample answer so staff still get help.
      setThinking(false); setWorkLabel('');
      const res = labelSampleCards(runQuery(q, { menuChips }));
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ ${err.message}\nThe assistant is unavailable, so the answer below uses SAMPLE data — do not act on it as a live record.` }, { role: 'assistant', streaming: true, ...res }]);
    }
  }, [thinking, live, startTour, menuChips]);

  const markDone = useCallback((idx) => { setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, streaming: false } : m))); }, []);
  const goto = useCallback((route) => { navigate(route); setOpen(false); }, [navigate]);
  const onChip = useCallback((chip) => { if (chip === TOUR_CHIP) startTour(); else send(chip); }, [send, startTour]);

  // Disabled by a super admin in System Settings → render nothing at all.
  if (!enabled) return null;

  /* -- collapsed FAB ---------------------------------------------------- */
  if (!open) {
    return (
      <button
        data-tour="assistant-fab"
        onClick={() => setOpen(true)}
        aria-label="Open Central Health Assistant"
        title="Ask the Central Health Assistant"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9995, width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 28px rgba(13,148,136,0.42), 0 2px 6px rgba(0,0,0,0.14)', transition: 'transform 0.15s, box-shadow 0.15s' }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
      >
        <MessageSquare size={24} strokeWidth={2.2} />
      </button>
    );
  }

  /* -- expanded panel --------------------------------------------------- */
  return (
    <div role="dialog" aria-label="Central Health Assistant" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9995, width: 384, maxWidth: 'calc(100vw - 32px)', height: 560, maxHeight: 'calc(100vh - 48px)', background: '#fff', borderRadius: 18, boxShadow: '0 20px 60px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'chatIn 0.22s cubic-bezier(.2,.8,.2,1)' }}>
      {/* header */}
      <div style={{ background: `linear-gradient(135deg, ${COLOR.dark}, ${COLOR.deep})`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 11, flexShrink: 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Bot size={20} color="#fff" strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
            Central Health Assistant
            {live && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34D399', boxShadow: '0 0 0 3px rgba(52,211,153,0.25)' }} />}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)' }}>
            {live ? `Connected · ${backendModel || 'assistant'}` : 'Here to help'}
          </div>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Close assistant" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.26)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}>
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} aria-live="polite" style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#F8FAFB' }}>
        {messages.map((m, idx) => (
          <div key={idx} style={{ marginBottom: 14, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '86%' }}>
              <div style={{ padding: '10px 12px', borderRadius: 14, fontSize: 13, lineHeight: 1.5, ...(m.role === 'user' ? { background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', borderBottomRightRadius: 5 } : { background: '#fff', color: '#334155', border: '1px solid #E8ECF0', borderBottomLeftRadius: 5 }) }}>
                {m.role === 'assistant' && m.streaming
                  ? <Typewriter text={m.text} onTick={scrollToEnd} onDone={() => markDone(idx)} />
                  : <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>}
              </div>

              {m.role === 'assistant' && !m.streaming && (
                <>
                  {m.steps && <StepsCard title={m.stepsTitle} steps={m.steps} />}
                  {m.cards?.map((card, i) => <RecordCard key={i} card={card} onNavigate={goto} />)}
                  {m.cta && (
                    <button onClick={() => goto(m.cta.route)} style={{ marginTop: 8, padding: '8px 14px', borderRadius: 9, border: 'none', background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {m.cta.label} <ArrowRight size={14} strokeWidth={2.5} />
                    </button>
                  )}
                  {m.chips && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      {m.chips.map((chip, i) => {
                        const isTour = chip === TOUR_CHIP;
                        return (
                          <button key={i} onClick={() => onChip(chip)} style={{ padding: '6px 11px', borderRadius: 20, cursor: 'pointer', border: `1px solid ${COLOR.primary}33`, background: isTour ? `${COLOR.primary}0D` : '#fff', color: COLOR.dark, fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = `${COLOR.primary}0D`; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = isTour ? `${COLOR.primary}0D` : '#fff'; }}>
                            {isTour && <Compass size={12} strokeWidth={2.4} />}
                            {chip}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {thinking && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <div style={{ padding: '12px 14px', borderRadius: 14, borderBottomLeftRadius: 5, background: '#fff', border: '1px solid #E8ECF0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#94A3B8', animation: `chatDot 1s ${i * 0.15}s infinite ease-in-out` }} />)}
              </div>
              {workLabel && <span style={{ fontSize: 11.5, color: '#64748B', fontWeight: 500 }}>{workLabel}</span>}
            </div>
          </div>
        )}
      </div>

      {/* composer */}
      <div style={{ padding: 12, borderTop: '1px solid #E8ECF0', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Ask about claims, payments, eligibility…"
            aria-label="Message the assistant"
            style={{ flex: 1, height: 42, borderRadius: 11, border: '1px solid #E2E8F0', padding: '0 14px', fontSize: 13, fontFamily: 'inherit', color: '#1E293B', outline: 'none', background: '#F8FAFB' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = COLOR.primary; e.currentTarget.style.background = '#fff'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#F8FAFB'; }}
          />
          <button onClick={() => send(input)} disabled={!input.trim() || thinking} aria-label="Send" style={{ width: 42, height: 42, borderRadius: 11, border: 'none', flexShrink: 0, background: input.trim() && !thinking ? `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})` : '#CBD5E1', color: '#fff', cursor: input.trim() && !thinking ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
            <Send size={17} strokeWidth={2.3} />
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', textAlign: 'center', marginTop: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <Sparkles size={10} /> {live ? (backendModel || 'assistant') : 'Offline demo · answers use sample data'}
        </div>
      </div>

      <style>{`
        @keyframes chatIn { 0% { opacity: 0; transform: translateY(12px) scale(0.98); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes chatDot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.5; } 30% { transform: translateY(-4px); opacity: 1; } }
      `}</style>
    </div>
  );
}
